import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filteredCategoryArticles = slug ? getArticlesByCategory(slug) : [];
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : getArticlesByCategory(categories[0]?.slug || slug || 'all');

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-foreground-50 text-2xl font-bold mb-4">分类不存在</h1>
          <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors">返回首页</Link>
        </div>
      </div>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `黑料718 - ${category.name}`,
        url: `http://gjxfl.cn/category/${slug}`,
        description: category.description,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://gjxfl.cn/' },
            { '@type': 'ListItem', position: 2, name: category.name, item: `http://gjxfl.cn/category/${slug}` },
          ],
        },
      },
    ],
  };

  return (
    <>
      <title>黑料718{category.name} | 最新{category.name}爆料汇总 | 黑料718官网</title>
      <meta name="description" content={category.description} />
      <meta name="keywords" content={`黑料718, ${category.name}, 718黑料, 吃瓜爆料, 黑料正能量718`} />
      <link rel="canonical" href={`http://gjxfl.cn/category/${slug}`} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <section className="relative pt-24 pb-16 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
              <nav className="flex items-center space-x-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                <span className="text-foreground-300">{category.name}</span>
              </nav>

              <div className="max-w-2xl">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <i className={`${category.icon} text-lg text-primary-400 w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-50">
                    {category.name}
                  </h1>
                </div>
                <p className="text-foreground-400 text-sm leading-relaxed">{category.description}</p>
              </div>
            </div>
          </section>

          <section className="w-full max-w-[1400px] mx-auto px-6 md:px-10 py-12">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex items-center space-x-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-200 text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
              <h2 className="text-foreground-100 text-lg font-semibold">
                共 <strong className="text-primary-400">{categoryArticles.length}</strong> 条爆料
              </h2>
            </div>

            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">暂无相关内容，敬请期待</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-48 object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent"></div>
                      {article.featured && (
                        <span className="absolute top-3 right-3 px-2 py-0.5 bg-accent-500/90 text-background-50 text-xs font-medium rounded">
                          热门
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-background-200 text-foreground-500 text-xs rounded-md">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-foreground-100 text-base font-semibold leading-snug mb-2 group-hover:text-primary-400 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-3">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-foreground-600 text-xs">
                        <span>{article.date}</span>
                        <div className="flex items-center space-x-3">
                          <span className="flex items-center space-x-1">
                            <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                            <span>{article.views >= 1000 ? `${(article.views / 1000).toFixed(0)}k` : article.views}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <i className="ri-chat-1-line w-3 h-3 flex items-center justify-center"></i>
                            <span>{article.comments >= 1000 ? `${(article.comments / 1000).toFixed(0)}k` : article.comments}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>

      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
    </>
  );
}